'use client'

import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useAppSelector } from 'features/hooks'
import { RootState } from 'features/store'
import type { Meal, MealRecipe, ShoppingContribution } from './types'

const STORAGE_PREFIX = 'culineer.meal-planner.v2.'

type Listener = () => void

// Meals belong to the account that is logged in. Until the real API exists they are kept in
// this browser, under a key that includes the account's user id, so two accounts on one
// browser never see each other's plans and logging out empties the in-memory copy.
let userId: string | null = null
let meals: Meal[] = []
let hydrated = false
let storageWatched = false
const listeners = new Set<Listener>()

function newId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  return `meal-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function storageKey() {
  return userId ? `${STORAGE_PREFIX}${userId}` : null
}

function readStored(): Meal[] {
  const key = storageKey()
  if (!key || typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(key)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function hydrate() {
  if (hydrated || typeof window === 'undefined') return
  hydrated = true
  meals = readStored()
}

function notify() {
  listeners.forEach((listener) => listener())
}

function watchOtherTabs() {
  if (storageWatched || typeof window === 'undefined') return
  storageWatched = true
  // Another tab saved a change: take its copy so this tab never overwrites it with a stale one.
  window.addEventListener('storage', (event) => {
    if (event.key !== storageKey()) return
    meals = readStored()
    hydrated = true
    notify()
  })
}

/** Point the store at the logged-in account. Pass null when nobody is logged in. */
export function bindMealPlannerUser(id: string | number | null | undefined) {
  const next = id == null || id === '' ? null : String(id)
  if (next === userId && hydrated) return
  userId = next
  hydrated = false
  meals = []
  hydrate()
  watchOtherTabs()
  notify()
}

function commit(next: Meal[]) {
  if (!userId) return false
  const previous = meals
  meals = next
  const key = storageKey()
  try {
    if (key) window.localStorage.setItem(key, JSON.stringify(next))
  } catch {
    // Storage is full or blocked: keep the last saved plan on screen instead of showing
    // changes that would vanish on refresh.
    meals = previous
    toast.error('Could not save your meal plan. Free up browser storage and try again.')
    notify()
    return false
  }
  notify()
  return true
}

function subscribe(listener: Listener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getMeals() {
  hydrate()
  return meals
}

export function useMeals() {
  const user = useAppSelector((state: RootState) => state.culineerAuth.user)
  const accountId = (user as { user_id?: string | number } | null | undefined)?.user_id ?? null
  const [current, setCurrent] = useState<Meal[]>([])
  const [ready, setReady] = useState(false)

  useEffect(() => {
    bindMealPlannerUser(accountId)
    setCurrent(getMeals())
    setReady(true)
    const unsubscribe = subscribe(() => setCurrent(getMeals()))
    return () => {
      unsubscribe()
    }
  }, [accountId])

  return { meals: current, ready }
}

export function mealsOnDate(list: Meal[], date: string) {
  return list
    .filter((meal) => meal.date === date)
    .sort((a, b) => (a.time || '99:99').localeCompare(b.time || '99:99') || a.name.localeCompare(b.name))
}

export function createMeal(input: {
  name: string
  date: string
  time?: string
  recipes?: MealRecipe[]
}) {
  const meal: Meal = {
    id: newId(),
    name: input.name.trim(),
    date: input.date,
    time: input.time || undefined,
    recipes: input.recipes || [],
  }
  return commit([...getMeals(), meal]) ? meal : null
}

export function updateMeal(
  id: string,
  patch: Partial<Pick<Meal, 'name' | 'date' | 'time'>>
) {
  commit(
    getMeals().map((meal) =>
      meal.id === id
        ? {
            ...meal,
            ...patch,
            name: patch.name !== undefined ? patch.name.trim() : meal.name,
            time: patch.time === '' ? undefined : patch.time ?? meal.time,
          }
        : meal
    )
  )
}

export function deleteMeal(id: string) {
  commit(getMeals().filter((meal) => meal.id !== id))
}

export function copyMeal(id: string, dates: string[]) {
  const source = getMeals().find((meal) => meal.id === id)
  if (!source || dates.length === 0) return []
  const copies = dates.map((date) => ({
    ...source,
    id: newId(),
    date,
    recipes: source.recipes.map((recipe) => ({
      ...recipe,
      shoppingContributions: [],
    })),
  }))
  return commit([...getMeals(), ...copies]) ? copies : []
}

export function addRecipes(mealId: string, recipes: MealRecipe[]) {
  commit(
    getMeals().map((meal) => {
      if (meal.id !== mealId) return meal
      const existing = new Set(meal.recipes.map((recipe) => recipe.id))
      const next = recipes.filter((recipe) => !existing.has(recipe.id))
      return { ...meal, recipes: [...meal.recipes, ...next] }
    })
  )
}

export function removeRecipe(mealId: string, recipeId: string) {
  commit(
    getMeals().map((meal) =>
      meal.id === mealId
        ? { ...meal, recipes: meal.recipes.filter((recipe) => recipe.id !== recipeId) }
        : meal
    )
  )
}

export function moveRecipe(fromMealId: string, recipeId: string, toMealId: string) {
  if (fromMealId === toMealId) return 'same' as const
  const all = getMeals()
  const from = all.find((meal) => meal.id === fromMealId)
  const recipe = from?.recipes.find((item) => item.id === recipeId)
  const to = all.find((meal) => meal.id === toMealId)
  if (!from || !recipe || !to) return 'missing' as const
  if (to.recipes.some((item) => item.id === recipeId)) return 'duplicate' as const
  commit(
    all.map((meal) => {
      if (meal.id === fromMealId) {
        return { ...meal, recipes: meal.recipes.filter((item) => item.id !== recipeId) }
      }
      if (meal.id === toMealId) {
        return { ...meal, recipes: [...meal.recipes, recipe] }
      }
      return meal
    })
  )
  return 'ok' as const
}

export function rememberShoppingContributions(
  mealId: string,
  contributions: Array<ShoppingContribution & { recipeId: string }>
) {
  commit(
    getMeals().map((meal) => {
      if (meal.id !== mealId) return meal
      return {
        ...meal,
        recipes: meal.recipes.map((recipe) => {
          const added = contributions.filter((item) => item.recipeId === recipe.id)
          if (!added.length) return recipe
          return {
            ...recipe,
            shoppingContributions: [
              ...recipe.shoppingContributions,
              ...added.map(({ listId, name, ingredientId }) => ({ listId, name, ingredientId })),
            ],
          }
        }),
      }
    })
  )
}

export function clearRecipeContributions(mealId: string, recipeId: string) {
  const meal = getMeals().find((item) => item.id === mealId)
  const recipe = meal?.recipes.find((item) => item.id === recipeId)
  const contributions = recipe?.shoppingContributions || []
  commit(
    getMeals().map((item) =>
      item.id === mealId
        ? {
            ...item,
            recipes: item.recipes.map((entry) =>
              entry.id === recipeId ? { ...entry, shoppingContributions: [] } : entry
            ),
          }
        : item
    )
  )
  return contributions
}

/** After an ingredient's shopping item moves lists, point its record at the new list. */
export function moveContributions(
  mealId: string,
  recipeId: string,
  ingredientId: string,
  fromListId: string,
  toListId: string
) {
  commit(
    getMeals().map((meal) =>
      meal.id !== mealId
        ? meal
        : {
            ...meal,
            recipes: meal.recipes.map((recipe) =>
              recipe.id !== recipeId
                ? recipe
                : {
                    ...recipe,
                    shoppingContributions: recipe.shoppingContributions.map((contribution) =>
                      contribution.ingredientId === ingredientId && contribution.listId === fromListId
                        ? { ...contribution, listId: toListId }
                        : contribution
                    ),
                  }
            ),
          }
    )
  )
}
